import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { sortBy } from 'lodash';

import { AppIcon, withStripes } from '@folio/stripes-core';
import { Button, Icon, Pane, Row, Col, Callout, Layout, Loading } from '@folio/stripes/components';
import DashboardsActionsMenu from './DashboardsActionsMenu';
import DashboardsMenu from './DashboardsMenu';
import DashboardForm from './DashboardForm';
import DashboardsContext from '../../../Context/dashboards/DashboardsContext';
import css from './style.css';
import Widget from '../widgetForm/components/Widget';
import useLocalization from '../../../helpers/useLocalization';
import useDashboardsMutations from '../../../hooks/useDashboardsMutations';
import useWidgetsAPI from '../../../hooks/useWidgetsAPI';
import useWidgetsMutations from '../../../hooks/useWidgetsMutations';

function DashboardWidgets(props) {
  const intl = useIntl();
  const { getLocalizedValue } = useLocalization();
  const dashboards = useContext(DashboardsContext);
  const { createDashboard, updateDashboard, deleteDashboard } = useDashboardsMutations();
  const { widgets } = useWidgetsAPI();
  const { deleteWidget } = useWidgetsMutations();

  const [currentDashboard, setCurrentDashboard] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDashboardForm, setShowDashboardForm] = useState(false);
  const [editingDashboard, setEditingDashboard] = useState(null);
  const calloutRef = React.useRef();

  const pathParts = props.location?.pathname?.split('/');
  const dashboardIdFromUrl = pathParts[3];

  useEffect(() => {
    setIsLoading(true);
    if (dashboardIdFromUrl && dashboards.length > 0) {
      const dashboard = dashboards.find(d => d.id === dashboardIdFromUrl);
      if (dashboard && dashboard.id !== currentDashboard?.id) setCurrentDashboard(dashboard);
      setIsLoading(false);
    } else if (dashboards.length > 0) {
      const defaultDash = dashboards.find(d => d.isDefault) || dashboards[0];
      if (defaultDash) {
        setCurrentDashboard(defaultDash);
        props.history.replace(`/reports/dashboards/${defaultDash.id}`);
      }
      setIsLoading(false);
    }
  }, [dashboardIdFromUrl, dashboards]);

  const widgetsList = currentDashboard?.id
    ? sortBy(widgets, ['displayOrder', 'id']).filter(w => w.dashboardId === currentDashboard.id)
    : [];

  const buildDashboardPayload = (values, existingId) => ({
    ...(existingId && { id: existingId }),
    name: values.name,
    desc: values.desc || [],
    category: values.category || 'operational',
    icon: values.icon || '',
    isDefault: values.isDefault || false,
    status: values.status || 'published',
    isActive: values.status !== 'archived',
    isVisible: values.status === 'published',
    displayOrder: values.displayOrder || dashboards.length + 1,
    permissions: {
      visibility: values.visibility || 'public',
      owner: values.owner || '',
      viewers: values.viewUsers || [],
      editors: values.editUsers || [],
    },
    styling: { theme: values.styling?.theme || 'light' },
    layout: { type: 'grid', columns: 12, rowHeight: 100, gap: 16, responsive: true },
    settings: { refreshInterval: 0, showFilters: true, enableExport: true },
  });

  const getFormInitialValues = (dashboard) => {
    if (!dashboard) return {
      name: [{ locale: 'en', value: '' }, { locale: 'ar', value: '' }],
      desc: [{ locale: 'en', value: '' }, { locale: 'ar', value: '' }],
      category: 'operational',
      isDefault: false,
      status: 'published',
      visibility: 'public',
      icon: '',
      styling: { theme: 'light' },
    };
    return {
      name: dashboard.name || [{ locale: 'en', value: '' }],
      desc: dashboard.desc || [{ locale: 'en', value: '' }],
      category: dashboard.category || 'operational',
      isDefault: dashboard.isDefault || false,
      status: dashboard.status || 'published',
      visibility: dashboard.permissions?.visibility || 'public',
      owner: dashboard.permissions?.owner || '',
      viewUsers: dashboard.permissions?.viewers || [],
      editUsers: dashboard.permissions?.editors || [],
      icon: dashboard.icon || '',
      styling: { theme: dashboard.styling?.theme || 'light' },
    };
  };

  const handleCreateDashboard = async (values) => {
    try {
      await createDashboard.mutateAsync(buildDashboardPayload(values));
      setShowDashboardForm(false);
      calloutRef.current?.sendCallout({ type: 'success', message: intl.formatMessage({ id: 'ui-reports.dashboard.callout.created' }) });
    } catch (error) {
      calloutRef.current?.sendCallout({ type: 'error', message: intl.formatMessage({ id: 'ui-reports.dashboard.callout.createError' }, { error: error.message }) });
    }
  };

  const handleEditDashboard = async (values) => {
    try {
      await updateDashboard.mutateAsync(buildDashboardPayload(values, editingDashboard.id));
      setShowDashboardForm(false);
      setEditingDashboard(null);
      calloutRef.current?.sendCallout({ type: 'success', message: intl.formatMessage({ id: 'ui-reports.dashboard.callout.updated' }) });
    } catch (error) {
      calloutRef.current?.sendCallout({ type: 'error', message: intl.formatMessage({ id: 'ui-reports.dashboard.callout.updateError' }, { error: error.message }) });
    }
  };

  const handleDeleteDashboard = async (dashboardId) => {
    try {
      await deleteDashboard.mutateAsync(dashboardId);
      setCurrentDashboard(null);
      props.history.replace('/reports/dashboards');
      calloutRef.current?.sendCallout({ type: 'success', message: intl.formatMessage({ id: 'ui-reports.dashboard.callout.deleted' }) });
    } catch (error) {
      calloutRef.current?.sendCallout({ type: 'error', message: intl.formatMessage({ id: 'ui-reports.dashboard.callout.deleteError' }, { error: error.message }) });
    }
  };

  const handleDeleteWidget = async (widgetId) => {
    try {
      await deleteWidget.mutateAsync(widgetId);
      calloutRef.current?.sendCallout({ type: 'success', message: intl.formatMessage({ id: 'ui-reports.dashboard.widget.callout.deleted' }) });
    } catch (error) {
      calloutRef.current?.sendCallout({ type: 'error', message: intl.formatMessage({ id: 'ui-reports.dashboard.widget.callout.deleteError' }, { error: error.message }) });
    }
  };

  const isSaving = createDashboard.isLoading || updateDashboard.isLoading;

  return (
    <>
      <Pane
        id="pane-dashboard-widgets"
        appIcon={<AppIcon app="reports" />}
        padContent={false}
        paneTitle={
          <DashboardsMenu
            dataOptions={dashboards}
            currentDashboard={currentDashboard}
            setCurrentDashboard={setCurrentDashboard}
          />
        }
        lastMenu={
          <DashboardsActionsMenu
            currentDashboard={currentDashboard}
            onCreateDashboard={() => { setEditingDashboard(null); setShowDashboardForm(true); }}
            onEditDashboard={(dashboard) => { setEditingDashboard(dashboard); setShowDashboardForm(true); }}
            onDeleteDashboard={handleDeleteDashboard}
          />
        }
      >
        {isLoading || !currentDashboard ? (
          <Layout className="centered full" style={{ minHeight: '400px' }}>
            <Loading size="xlarge" />
          </Layout>
        ) : !widgetsList.length ? (
          <div className={css.emptyList}>
            <div style={{ paddingBottom: '10px' }}>
              <FormattedMessage id="ui-reports.dashboards.widgets.noWidgets.message" defaultMessage="No widgets found!" />
            </div>
            <Button
              marginBottom0
              id="dashboards-widgets-list-new-widget-btn"
              to={{ pathname: `/reports/dashboards/${currentDashboard.id}/widgets/create` }}
            >
              <Icon icon="plus-sign">
                <FormattedMessage id="ui-reports.dashboards.widgets.addNewWidget.btn" defaultMessage="Create new widget" />
              </Icon>
            </Button>
          </div>
        ) : (
          <div style={{ padding: '20px' }}>
            <Row>
              {widgetsList.map((widget, index) => {
                const widgetName = getLocalizedValue(widget?.name);
                const w = widget?.layout?.w || 6;
                const colSize = w >= 12 ? 12 : w >= 8 ? 8 : w >= 6 ? 6 : 4;
                return (
                  <Col
                    key={widget.id}
                    xs={12}
                    sm={colSize}
                    md={colSize}
                    lg={colSize}
                  >
                    <Widget
                      widget={widget}
                      widgetName={widgetName}
                      index={index}
                      onDeleteWidget={handleDeleteWidget}
                      currentDashboard={currentDashboard}
                    />
                  </Col>
                );
              })}
            </Row>
          </div>
        )}
      </Pane>

      <DashboardForm
        open={showDashboardForm}
        onClose={() => { setShowDashboardForm(false); setEditingDashboard(null); }}
        onSubmit={editingDashboard ? handleEditDashboard : handleCreateDashboard}
        initialValues={getFormInitialValues(editingDashboard)}
        isLoading={isSaving}
      />

      <Callout ref={calloutRef} />
    </>
  );
}

DashboardWidgets.propTypes = {
  history: PropTypes.shape({ push: PropTypes.func.isRequired, replace: PropTypes.func.isRequired }).isRequired,
  location: PropTypes.object,
};

export default withStripes(DashboardWidgets);
